import { createMomentCodec } from '@/_pure/directionMoment';
import vocabularyJson from '@/adapters/primitive/_data/directions/momentVocabulary.json' with { type: 'json' };
import type { Moment, MomentPart, MomentScalar, MomentVocabulary } from '@/_contract/_~generated/directionMomentSchema';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';

/** Schema conformance is asserted by tests; JSON imports widen literal unions. */
export const PRIMITIVE_MOMENT_VOCABULARY = vocabularyJson as unknown as MomentVocabulary;

export const PRIMITIVE_MOMENT_CODEC = createMomentCodec(PRIMITIVE_MOMENT_VOCABULARY);

/** Encodes a primitive moment as its canonical replay key. */
export function encodePrimitiveMoment(moment: Moment): string {
    return PRIMITIVE_MOMENT_CODEC.encode(moment);
}

/** Builds a moment part; `ordinal` is the 1-based number shown in step keys. */
export function momentPart(kind: string, parameters?: Readonly<Record<string, MomentScalar | undefined>>, ordinal?: number): MomentPart {
    if (ordinal !== undefined && !(Number.isInteger(ordinal) && ordinal >= 1)) throw new Error(`Moment part '${kind}' has invalid ordinal ${ordinal}.`);
    const defined = Object.entries(parameters ?? {}).filter((entry): entry is [string, MomentScalar] => entry[1] !== undefined);
    return {
        kind,
        ...(ordinal === undefined ? {} : { ordinal: { index: ordinal - 1 } }),
        ...(defined.length > 0 ? { parameters: Object.fromEntries(defined) } : {}),
    };
}

export function stepMoment(kind: string, parameters?: Readonly<Record<string, MomentScalar | undefined>>, ordinal?: number): Moment {
    return { frames: [], step: momentPart(kind, parameters, ordinal) };
}

/** The moment and its derived step key, for spreading into a step. */
export function momentKey(moment: Moment): Pick<PrimitiveStepState, 'moment' | 'stepKey'> {
    return { moment, stepKey: encodePrimitiveMoment(moment) };
}

function requireMoment(step: Pick<PrimitiveStepState, 'moment' | 'stepKey'>): Moment {
    if (!step.moment) throw new Error(`Primitive step '${step.stepKey}' has no moment.`);
    return step.moment;
}

/** Nests a step's moment inside an outer frame and re-derives its key. */
export function withMomentFrame<T extends Pick<PrimitiveStepState, 'moment' | 'stepKey'>>(step: T, frame: MomentPart): T {
    const moment = requireMoment(step);
    return { ...step, ...momentKey({ frames: [frame, ...moment.frames], step: moment.step }) };
}

/** Adds step parameters to a step's moment and re-derives its key. */
export function withMomentParameters<T extends Pick<PrimitiveStepState, 'moment' | 'stepKey'>>(
    step: T,
    parameters: Readonly<Record<string, MomentScalar>>,
): T {
    const moment = requireMoment(step);
    return {
        ...step,
        ...momentKey({ frames: moment.frames, step: { ...moment.step, parameters: { ...moment.step.parameters, ...parameters } } }),
    };
}
